import { supabase } from '@/lib/supabase/client'
import { ChatOption } from '@/lib/chat-data'

export type KnowledgeBaseEntry = {
  id: string
  keywords: string[]
  trigger_pattern?: string
  is_regex?: boolean
  response_text: string
  is_active: boolean
  created_at: string
}

export type KnowledgeBaseInsert = Omit<KnowledgeBaseEntry, 'id' | 'created_at'>

export type DialogueStep = {
  id: string
  step_identifier: string
  step_key?: string
  message_text: string
  question_text?: string
  options: ChatOption[]
  sort_order: number
  created_at: string
  is_start_node?: boolean
}

export type DialogueStepInsert = Omit<DialogueStep, 'id' | 'created_at'>

export type ChatbotVersion = {
  id: string
  description: string
  created_at: string
  snapshot: any
}

// Knowledge Base
export const getKnowledgeBase = async () => {
  const { data, error } = await supabase
    .from('chatbot_knowledge_base')
    .select('*')
    .order('created_at', { ascending: false })

  return { data: data as KnowledgeBaseEntry[], error }
}

export const createKnowledgeBaseEntry = async (entry: KnowledgeBaseInsert) => {
  const { data, error } = await supabase
    .from('chatbot_knowledge_base')
    .insert([entry])
    .select()
    .single()

  return { data: data as KnowledgeBaseEntry, error }
}

export const updateKnowledgeBaseEntry = async (
  id: string,
  entry: Partial<KnowledgeBaseInsert>,
) => {
  const { data, error } = await supabase
    .from('chatbot_knowledge_base')
    .update(entry)
    .eq('id', id)
    .select()
    .single()

  return { data: data as KnowledgeBaseEntry, error }
}

export const deleteKnowledgeBaseEntry = async (id: string) => {
  const { error } = await supabase
    .from('chatbot_knowledge_base')
    .delete()
    .eq('id', id)

  return { error }
}

// Dialogue Steps
export const getDialogueSteps = async () => {
  const { data, error } = await supabase
    .from('chatbot_dialogue_steps')
    .select('*')
    .order('sort_order', { ascending: true })

  // Ensure options is parsed correctly if it comes as string
  const formattedData = data?.map((step) => ({
    ...step,
    options:
      typeof step.options === 'string'
        ? JSON.parse(step.options)
        : step.options,
  }))

  return { data: formattedData as DialogueStep[], error }
}

export const updateDialogueStep = async (
  id: string,
  step: Partial<DialogueStepInsert>,
) => {
  const { data, error } = await supabase
    .from('chatbot_dialogue_steps')
    .update(step)
    .eq('id', id)
    .select()
    .single()

  return { data: data as DialogueStep, error }
}

// Versioning
export const getChatbotVersions = async () => {
  const { data, error } = await supabase
    .from('chatbot_config_versions')
    .select('id, description, created_at')
    .order('created_at', { ascending: false })

  return { data: data as Omit<ChatbotVersion, 'snapshot'>[], error }
}

export const createChatbotVersion = async (description: string) => {
  const { data, error } = await supabase.rpc('create_chatbot_version', {
    p_description: description,
  })

  return { data, error }
}

export const restoreChatbotVersion = async (versionId: string) => {
  const { data, error } = await supabase.rpc('restore_chatbot_version', {
    p_version_id: versionId,
  })

  return { data, error }
}
